import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Card, Row, Col, Table, Modal, Descriptions, Spin, Image, Button, Steps, Alert, Space } from "antd";
import { DataBadge } from "../../components/ui";
import { notify } from "../../components/ui/notify";
import { solicitudesCambioService } from "../../components/solicitudes-cambio/solicitudesCambioService";
import { wizardService } from "../../components/solicitudes-cambio/wizardService";
import { capturarPantalla } from "../../components/solicitudes-cambio/capturarPantalla";
import { recolectarInfoTecnica } from "../../components/solicitudes-cambio/infoTecnica";
import SolicitarCambioModal from "../../components/solicitudes-cambio/SolicitarCambioModal";
import { PATH } from "../../redux/utils";

import "../../components/tribunal/TribunalPage.css";
import "../../components/tribunal/TribunalTablePage.css";

dayjs.locale("es");

const TIPO_LABEL = { error: "Error", cambio_visual: "Cambio visual", nueva_funcion: "Nueva función", otro: "Otro" };
const ESTADO_LABEL = {
  recibida: "Recibida",
  en_revision: "En revisión",
  aprobada: "Aprobada",
  fuera_de_alcance: "Fuera de alcance",
  rechazada: "Rechazada",
  en_staging: "En staging",
  lista_para_validar: "Lista para validar",
  publicada: "Publicada",
  cotizacion_enviada: "Cotización enviada",
};
const ESTADO_COLOR = {
  recibida: "#6B7280",
  en_revision: "#D97706",
  aprobada: "#0A539A",
  fuera_de_alcance: "#9333EA",
  rechazada: "#DC2626",
  en_staging: "#2563EB",
  lista_para_validar: "#16A34A",
  publicada: "#15803D",
  cotizacion_enviada: "#CA8A04",
};
const ESTADOS_CERRADOS = new Set(["publicada", "rechazada", "fuera_de_alcance"]);

const formatFecha = (d) => {
  if (!d) return "—";
  const x = dayjs(d);
  return x.isValid() ? x.format("DD MMM YYYY, HH:mm") : String(d);
};

function ImagenProtegida({ url, alt }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let objectUrl;
    let cancelled = false;
    fetch(url, { headers: solicitudesCambioService.authHeader() })
      .then((r) => r.blob())
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  if (!src) return <div style={{ width: 120, height: 80, background: "#f3f4f6" }} />;
  return <Image src={src} alt={alt} width={120} height={80} style={{ objectFit: "cover" }} />;
}

function CotizacionCard({ detalle, onRespondido }) {
  const [enviando, setEnviando] = useState(false);
  if (detalle.estado !== "cotizacion_enviada" || detalle.cotizacion_respuesta !== "pendiente") return null;

  const responder = async (acepta) => {
    setEnviando(true);
    try {
      const actualizado = await solicitudesCambioService.responderCotizacion(detalle.id, acepta);
      notify.success(acepta ? "Aceptaste la cotización." : "Avisamos que por ahora no.");
      onRespondido(actualizado);
    } catch (err) {
      notify.error("No se pudo enviar tu respuesta. Inténtalo de nuevo.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Alert
      type="warning"
      showIcon
      className="mb-3"
      message="Te enviamos una cotización"
      description={
        <div>
          {detalle.cotizacion_monto != null && (
            <div className="fw-semibold mb-1">Monto: ${Number(detalle.cotizacion_monto).toLocaleString("es-MX")}</div>
          )}
          {detalle.nota_proveedor && <div className="mb-2" style={{ whiteSpace: "pre-wrap" }}>{detalle.nota_proveedor}</div>}
          <Space>
            <Button type="primary" size="small" loading={enviando} onClick={() => responder(true)}>Aceptar</Button>
            <Button size="small" loading={enviando} onClick={() => responder(false)}>No por ahora</Button>
          </Space>
        </div>
      }
    />
  );
}

function DetalleModal({ idSolicitud, onClose }) {
  const [loading, setLoading] = useState(true);
  const [detalle, setDetalle] = useState(null);

  const cargar = useCallback(() => {
    if (!idSolicitud) return;
    setLoading(true);
    solicitudesCambioService.obtenerDetalle(idSolicitud).then(setDetalle).finally(() => setLoading(false));
  }, [idSolicitud]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <Modal open={!!idSolicitud} onCancel={onClose} footer={null} title={detalle ? `${detalle.folio} · ${detalle.titulo}` : "Solicitud"} width={680} zIndex={3200}>
      {loading && <div className="text-center py-4"><Spin /></div>}
      {!loading && detalle && (
        <>
          <Descriptions column={2} size="small" bordered className="mb-3">
            <Descriptions.Item label="Tipo">{TIPO_LABEL[detalle.tipo] || detalle.tipo}</Descriptions.Item>
            <Descriptions.Item label="Estado">
              <DataBadge color={ESTADO_COLOR[detalle.estado]}>{ESTADO_LABEL[detalle.estado] || detalle.estado}</DataBadge>
            </Descriptions.Item>
            <Descriptions.Item label="Enviada" span={2}>{formatFecha(detalle.created_at)}</Descriptions.Item>
          </Descriptions>

          <CotizacionCard detalle={detalle} onRespondido={setDetalle} />

          <div className="fw-semibold mb-1">{detalle.resumen_cliente ? "Lo que nos contaste" : "Descripción"}</div>
          <div className="mb-3" style={{ whiteSpace: "pre-wrap" }}>{detalle.resumen_cliente || detalle.descripcion}</div>

          {detalle.nota_proveedor && detalle.estado !== "cotizacion_enviada" && (
            <>
              <div className="fw-semibold mb-1">Nota del proveedor</div>
              <div className="mb-3" style={{ whiteSpace: "pre-wrap" }}>{detalle.nota_proveedor}</div>
            </>
          )}

          {[...(detalle.archivos || []), ...(detalle.archivos_wizard || [])].length > 0 && (
            <div className="d-flex flex-wrap gap-2 mb-3">
              {(detalle.archivos || []).map((a) => (
                <ImagenProtegida key={`c-${a.id}`} url={`${PATH}${a.url}`} alt={a.tipo} />
              ))}
              {(detalle.archivos_wizard || []).map((a) => (
                <ImagenProtegida key={`w-${a.id}`} url={`${PATH}${a.url}`} alt={a.nombre_original} />
              ))}
            </div>
          )}

          {Array.isArray(detalle.historial) && detalle.historial.length > 0 && (
            <>
              <div className="fw-semibold mb-2">Línea de tiempo</div>
              <Steps
                direction="vertical"
                size="small"
                current={detalle.historial.length - 1}
                items={detalle.historial.map((h) => ({
                  title: ESTADO_LABEL[h.estado] || h.estado,
                  description: (
                    <span className="small text-secondary">
                      {formatFecha(h.created_at)}{h.nota ? ` · ${h.nota}` : ""}
                    </span>
                  ),
                }))}
              />
            </>
          )}
        </>
      )}
    </Modal>
  );
}

function HistorialModal({ open, items, borradores, loading, onClose, onVerDetalle, onContinuarBorrador }) {
  const filas = useMemo(
    () => [
      ...borradores.map((b) => ({ ...b, _esBorrador: true })),
      ...items.map((r) => ({ ...r, _esBorrador: false })),
    ],
    [items, borradores]
  );

  const columns = [
    { title: "Folio", key: "folio", width: 110, render: (_, r) => (r._esBorrador ? "—" : r.folio) },
    { title: "Título", key: "titulo", render: (_, r) => (r._esBorrador ? r.nombre : r.titulo) },
    { title: "Tipo", key: "tipo", render: (_, r) => (r._esBorrador ? "Nueva función" : (TIPO_LABEL[r.tipo] || r.tipo)) },
    {
      title: "Estado",
      key: "estado",
      render: (_, r) =>
        r._esBorrador ? (
          <DataBadge color="#9CA3AF">Borrador</DataBadge>
        ) : (
          <DataBadge color={ESTADO_COLOR[r.estado]}>{ESTADO_LABEL[r.estado] || r.estado}</DataBadge>
        ),
    },
    {
      title: "Enviada",
      key: "created_at",
      render: (_, r) => formatFecha(r._esBorrador ? r.updated_at : r.created_at),
    },
    {
      title: "Acciones",
      key: "acciones",
      align: "right",
      render: (_, r) =>
        r._esBorrador ? (
          <Button onClick={() => onContinuarBorrador(r.id_wizard)}>Continuar</Button>
        ) : (
          <Button type="primary" onClick={() => onVerDetalle(r.id)}>Ver detalles</Button>
        ),
    },
  ];

  return (
    <Modal open={open} onCancel={onClose} footer={null} title="Historial de solicitudes" width={820} zIndex={3100}>
      <Table columns={columns} dataSource={filas} rowKey={(r) => (r._esBorrador ? `w-${r.id_wizard}` : `s-${r.id}`)} pagination={{ pageSize: 8 }} loading={loading} />
    </Modal>
  );
}

function OpcionCard({ icon, titulo, descripcion, onClick }) {
  return (
    <Card
      hoverable
      onClick={onClick}
      bodyStyle={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 10, padding: "32px 20px" }}
      style={{ height: "100%", cursor: "pointer" }}
    >
      <div
        className="d-inline-flex align-items-center justify-content-center"
        style={{ width: 56, height: 56, borderRadius: "50%", background: "#DCE9F5", color: "#0A539A", fontSize: 22 }}
      >
        <i className={icon} />
      </div>
      <div className="fw-semibold fs-5">{titulo}</div>
      <div className="small text-secondary">{descripcion}</div>
    </Card>
  );
}

export default function MisSolicitudesPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [borradores, setBorradores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idSeleccionado, setIdSeleccionado] = useState(null);
  const [historialAbierto, setHistorialAbierto] = useState(false);
  const [sesion, setSesion] = useState(null); // { captura, info, tipoInicial }
  const [capturando, setCapturando] = useState(false);

  const cargar = useCallback(() => {
    setLoading(true);
    Promise.all([
      solicitudesCambioService.listarMias({ limit: 200, offset: 0 }),
      wizardService.listarMisBorradores(),
    ])
      .then(([solicitudes, wizard]) => {
        setItems(solicitudes.items || []);
        setBorradores(wizard.items || []);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const kpis = useMemo(() => {
    const total = items.length;
    const cumplidos = items.filter((r) => r.estado === "publicada").length;
    const activos = items.filter((r) => !ESTADOS_CERRADOS.has(r.estado)).length;
    return { total, activos, cumplidos };
  }, [items]);

  const abrirReporteError = useCallback(async () => {
    if (capturando) return;
    setCapturando(true);
    const info = recolectarInfoTecnica(pathname, "Mis solicitudes");
    try {
      const captura = await capturarPantalla();
      setSesion({ captura, info, tipoInicial: "error" });
    } catch (err) {
      notify.error("No se pudo tomar la captura de pantalla. Inténtalo de nuevo.");
    } finally {
      setCapturando(false);
    }
  }, [pathname, capturando]);

  const cerrarSesion = () => setSesion(null);

  const enviado = (folio) => {
    cerrarSesion();
    notify.success(`Solicitud ${folio} enviada. Te avisaremos cuando esté en revisión.`);
    cargar();
  };

  return (
    <main className="trib-main">
      <div className="trib-wrap">
        <div className="laboral-filters-panel">
          <div className="laboral-panel-header">
            <h1 className="laboral-panel-title">Mis solicitudes de cambio</h1>
            <p className="laboral-panel-subtitle">Seguimiento de los errores y cambios que has solicitado.</p>
          </div>
        </div>

        <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
          <Col xs={24} sm={8}>
            <Card loading={loading} bodyStyle={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div className="fs-3 fw-bold">{kpis.total}</div>
              <div className="small text-secondary">Total de solicitudes</div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card loading={loading} bodyStyle={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div className="fs-3 fw-bold">{kpis.activos}</div>
              <div className="small text-secondary">Activas / en proceso</div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card loading={loading} bodyStyle={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div className="fs-3 fw-bold">{kpis.cumplidos}</div>
              <div className="small text-secondary">Cumplidas / publicadas</div>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <OpcionCard
              icon="fa-solid fa-clock-rotate-left"
              titulo="Historial de solicitudes"
              descripcion="Consulta el estado y los detalles de todo lo que has enviado."
              onClick={() => setHistorialAbierto(true)}
            />
          </Col>
          <Col xs={24} md={8}>
            <OpcionCard
              icon="fa-solid fa-bug"
              titulo="Reportar un error o pedir una corrección"
              descripcion="Toma una captura de la pantalla actual y describe qué está fallando."
              onClick={abrirReporteError}
            />
          </Col>
          <Col xs={24} md={8}>
            <OpcionCard
              icon="fa-solid fa-wand-magic-sparkles"
              titulo="Solicitar un módulo o función nueva"
              descripcion="Unas preguntas sencillas para entender qué necesitas, sin nada técnico."
              onClick={() => navigate("/solicitudes/nueva-funcion")}
            />
          </Col>
        </Row>
      </div>

      <HistorialModal
        open={historialAbierto}
        items={items}
        borradores={borradores}
        loading={loading}
        onClose={() => setHistorialAbierto(false)}
        onVerDetalle={setIdSeleccionado}
        onContinuarBorrador={(idWizard) => navigate(`/solicitudes/nueva-funcion/${idWizard}`)}
      />
      <DetalleModal idSolicitud={idSeleccionado} onClose={() => setIdSeleccionado(null)} />
      {sesion && (
        <SolicitarCambioModal
          captura={sesion.captura}
          info={sesion.info}
          tipoInicial={sesion.tipoInicial}
          onClose={cerrarSesion}
          onEnviado={enviado}
        />
      )}
    </main>
  );
}
